import React, { useEffect, useMemo } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Select from "components/ui/Select";
import useModalStore from "store/modals";
import { useAllColors } from "http-hooks/color/useColor";

const SelectColors = ({
  value,
  onChange,
  placeholder = "Search colors...",
  isDisabled = false,
  isRequired = false,
  label = "Color",
  className = "",
  filters = {},
  showLabel = true,
  isMulti = false,
  showAddButton = true, // New prop to control plus button visibility
}) => {
  const { openModal } = useModalStore();
  
  const {
    isLoading: isLoadingAllColors,
    data: { data: allColors } = [],
  } = useAllColors({
    clrname: "",
    ag_status: "",
    noPagination: true,
    ...filters,
  });

  // Convert colors to options for Select component - memoized for performance
  const colorOptions = useMemo(() => allColors?.map(color => ({
    value: color.clrId,
    label: color.clrname,
    clrname: color.clrname,
    ...color
  })) || [], [allColors]);

  // Auto-select the matching option when colors load and value is provided as simple object
  useEffect(() => {
    // If value is provided as a simple object with clrname but not as a full option
    if (value && !value.value && value.clrname && colorOptions.length > 0 && onChange) {
      const matchedOption = colorOptions.find(option => 
        String(option.clrname) === String(value.clrname)
      );
      
      if (matchedOption && matchedOption !== value) {
        onChange(isMulti ? [matchedOption] : matchedOption);
      }
    }
  }, [value, colorOptions, onChange, isMulti]);

  const handleAddNew = () => {
    openModal('color');
  };

  return (
    <div>
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
          {showAddButton && !isDisabled && (
            <button
              type="button"
              onClick={handleAddNew}
              className="ml-2 p-1 text-xs font-bold bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200 flex items-center"
              title="Add new color"
            >
              <PlusIcon className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <Select
        value={value}
        onChange={onChange}
        options={colorOptions}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isLoading={isLoadingAllColors}
        isRequired={isRequired}
        label={label}
        className={className}
        showLabel={false}
        isClearable={true}
        isSearchable={true}
        isMulti={isMulti}
      />
      {!showLabel && showAddButton && !isDisabled && (
        <button
          type="button"
          onClick={handleAddNew}
          className="absolute top-0 right-0 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
          title="Add new color"
        >
          <PlusIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default SelectColors; 